import { supabase } from '../lib/supabase';
import { applyDomainFilterToQuery, applyDomainOnlyFilterToQuery } from '../hooks/useDomainQuery';
import { ArtWork, ArtCategory } from './portfolioService';

/**
 * Récupère toutes les œuvres du portfolio pour l'administration
 */
export const fetchAdminArtworks = async (
  viewMode: 'all' | 'published' | 'draft' = 'all',
  searchQuery: string = '',
  categoryId: string = ''
) => {
  try {
    // Construire la requête de base
    let query = supabase
      .from('portfolio_artworks')
      .select(`
        id,
        title,
        slug,
        description,
        content,
        featured_image_url,
        materials,
        dimensions,
        location,
        year,
        gallery_images,
        making_of_images,
        status,
        created_at,
        updated_at
      `)
      .order('created_at', { ascending: false });

    // Appliquer le filtre domaine avec fallback
    query = applyDomainFilterToQuery(query);
    
    // Filtrer par statut si nécessaire
    if (viewMode !== 'all') {
      query = query.eq('status', viewMode);
    }
    
    // Filtrer par recherche si nécessaire
    if (searchQuery) {
      query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`);
    }
    
    const { data: artworks, error } = await query;
    
    if (error) {
      console.error('Erreur lors de la récupération des œuvres:', error);
      throw error;
    }
    
    // Filtrer par catégorie si nécessaire
    let filteredArtworks = artworks || [];
    
    if (categoryId) {
      // Récupérer les IDs des œuvres associées à la catégorie
      const { data: artworkCategories, error: categoriesError } = await supabase
        .from('portfolio_artwork_categories')
        .select('artwork_id')
        .eq('category_id', categoryId);
      
      if (categoriesError) {
        console.error('Erreur lors de la récupération des catégories:', categoriesError);
        throw categoriesError;
      }
      
      // Filtrer les œuvres par ID
      const artworkIds = artworkCategories?.map(pc => pc.artwork_id) || [];
      filteredArtworks = filteredArtworks.filter(artwork => artworkIds.includes(artwork.id));
    }
    
    // Récupérer les catégories pour chaque œuvre
    const formattedArtworks = await Promise.all(filteredArtworks.map(async (artwork) => {
      const { data: categories, error: categoriesError } = await supabase
        .from('portfolio_artwork_categories')
        .select(`
          category:portfolio_categories(id, name)
        `)
        .eq('artwork_id', artwork.id);
      
      if (categoriesError) {
        console.error('Erreur lors de la récupération des catégories:', categoriesError);
        return {
          ...artwork,
          categories: []
        };
      }
      
      return {
        ...artwork,
        categories: categories?.map((cat: any) => cat.category) || [],
        materials: Array.isArray(artwork.materials) ? artwork.materials : [],
        gallery_images: Array.isArray(artwork.gallery_images) ? artwork.gallery_images : [],
        making_of_images: Array.isArray(artwork.making_of_images) ? artwork.making_of_images : []
      };
    }));
    
    return formattedArtworks;
  } catch (error) {
    console.error('Erreur lors de la récupération des œuvres:', error);
    throw error;
  }
};

/**
 * Récupère une œuvre par son ID pour l'administration
 */
export const fetchAdminArtworkById = async (id: string) => {
  try {
    // Récupérer l'œuvre avec filtrage domaine
    let query = supabase
      .from('portfolio_artworks')
      .select('*')
      .eq('id', id);
    
    // Appliquer le filtre domaine avec fallback
    query = applyDomainFilterToQuery(query);
    
    const { data: artwork, error } = await query.single();
    
    if (error) {
      console.error('Erreur lors de la récupération de l\'œuvre:', error);
      throw error;
    }
    
    if (!artwork) {
      return null;
    }
    
    // Récupérer les catégories de l'œuvre
    const { data: categories, error: categoriesError } = await supabase
      .from('portfolio_artwork_categories')
      .select(`
        category:portfolio_categories(id, name)
      `)
      .eq('artwork_id', artwork.id);
    
    if (categoriesError) {
      console.error('Erreur lors de la récupération des catégories:', categoriesError);
    }
    
    return {
      ...artwork,
      categories: categories?.map((cat: any) => cat.category) || [],
      materials: Array.isArray(artwork.materials) ? artwork.materials : [],
      gallery_images: Array.isArray(artwork.gallery_images) ? artwork.gallery_images : [],
      making_of_images: Array.isArray(artwork.making_of_images) ? artwork.making_of_images : []
    };
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'œuvre:', error);
    throw error;
  }
};

/**
 * Récupère toutes les catégories d'œuvres pour l'administration
 * @param domain - Domaine pour filtrer les catégories (optionnel)
 */
export const fetchAdminArtCategories = async (domain?: string) => {
  try {
    // Récupérer les catégories de la table avec filtre par domaine si fourni
    let query = supabase
      .from('portfolio_categories')
      .select('*')
      .order('name', { ascending: true });

    // Appliquer le filtre domaine uniquement si fourni
    if (domain) {
      query = applyDomainOnlyFilterToQuery(query);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      throw error;
    }

    // Récupérer le nombre d'œuvres par catégorie
    const categoriesWithCount = await Promise.all(
      (data || []).map(async (category) => {
        const { count, error: countError } = await supabase
          .from('portfolio_artwork_categories')
          .select('*', { count: 'exact', head: true })
          .eq('category_id', category.id);

        if (countError) {
          console.error('Erreur lors du comptage des œuvres:', countError);
          return { ...category, artwork_count: 0 };
        }

        return { ...category, artwork_count: count || 0 };
      })
    );

    return categoriesWithCount;
  } catch (error) {
    console.error('Erreur lors de la récupération des catégories:', error);
    throw error;
  }
};

/**
 * Crée une nouvelle œuvre
 * Note: Cette fonction ne peut pas utiliser useDomainQuery car elle est appelée depuis les composants
 * Les composants doivent passer tenantId et domain en paramètres
 */
export const createArtwork = async (artworkData: Partial<ArtWork>, tenantId: string, domain?: string) => {
  try {
    // Ajouter le tenant_id et domain aux données de l'œuvre
    const artworkWithTenant = {
      ...artworkData,
      tenant_id: tenantId,
      domain: domain
    };
    
    const { data, error } = await supabase
      .from('portfolio_artworks')
      .insert(artworkWithTenant)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la création de l\'œuvre:', error);
    throw error;
  }
};

/**
 * Met à jour une œuvre existante
 */
export const updateArtwork = async (id: string, artworkData: Partial<ArtWork>, tenantId: string, domain?: string) => {
  try {
    // Ajouter domain aux données si fourni
    const updateData = domain ? { ...artworkData, domain } : artworkData;
    
    let query = supabase
      .from('portfolio_artworks')
      .update(updateData)
      .eq('id', id);
    
    // Appliquer le filtre domaine avec fallback
    query = applyDomainFilterToQuery(query);
    
    const { data, error } = await query.select().single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de l\'œuvre:', error);
    throw error;
  }
};

/**
 * Supprime une œuvre
 */
export const deleteArtwork = async (id: string) => {
  try {
    // Vérifier que l'œuvre appartient bien au domaine avant de la supprimer
    let query = supabase
      .from('portfolio_artworks')
      .select('id')
      .eq('id', id);
    
    // Appliquer le filtre domaine avec fallback
    query = applyDomainFilterToQuery(query);
    
    const { data: artwork, error: artworkError } = await query.single();
    
    if (artworkError || !artwork) {
      throw new Error('Œuvre non trouvée ou accès non autorisé');
    }
    
    // Supprimer d'abord les relations avec les catégories
    const { error: categoriesError } = await supabase
      .from('portfolio_artwork_categories')
      .delete()
      .eq('artwork_id', id);
    
    if (categoriesError) throw categoriesError;
    
    // Puis supprimer l'œuvre elle-même
    const { error } = await supabase
      .from('portfolio_artworks')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'œuvre:', error);
    throw error;
  }
};

/**
 * Met à jour les catégories d'une œuvre
 */
export const updateArtworkCategories = async (artworkId: string, categoryIds: string[]) => {
  try {
    // Supprimer toutes les associations existantes
    const { error: deleteError } = await supabase
      .from('portfolio_artwork_categories')
      .delete()
      .eq('artwork_id', artworkId);
    
    if (deleteError) throw deleteError;
    
    // Si aucune catégorie n'est sélectionnée, on s'arrête là
    if (!categoryIds || categoryIds.length === 0) {
      return true;
    }
    
    // Créer les nouvelles associations
    const newCategories = categoryIds.map(categoryId => ({
      artwork_id: artworkId,
      category_id: categoryId,
      created_at: new Date().toISOString()
    }));
    
    const { error: insertError } = await supabase
      .from('portfolio_artwork_categories')
      .insert(newCategories);
    
    if (insertError) throw insertError;
    
    return true;
  } catch (error) {
    console.error('Erreur lors de la mise à jour des catégories:', error);
    throw error;
  }
};

/**
 * Crée une nouvelle catégorie
 */
export const createCategory = async (categoryData: Partial<ArtCategory>) => {
  try {
    const { data, error } = await supabase
      .from('portfolio_categories')
      .insert(categoryData)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la création de la catégorie:', error);
    throw error;
  }
};

/**
 * Met à jour une catégorie existante
 */
export const updateCategory = async (id: string, categoryData: Partial<ArtCategory>) => {
  try {
    const { data, error } = await supabase
      .from('portfolio_categories')
      .update(categoryData)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la catégorie:', error);
    throw error;
  }
};

/**
 * Supprime une catégorie
 */
export const deleteCategory = async (id: string) => {
  try {
    // Supprimer d'abord les relations avec les œuvres
    const { error: categoriesError } = await supabase
      .from('portfolio_artwork_categories')
      .delete()
      .eq('category_id', id);
    
    if (categoriesError) throw categoriesError;
    
    // Puis supprimer la catégorie elle-même
    const { error } = await supabase
      .from('portfolio_categories')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression de la catégorie:', error);
    throw error;
  }
};
